import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientLogger } from '../utils/logger';
import FirebaseLogin from '../components/FirebaseLogin';
import { isLondonBookFairActive, isSummerWelcomeActive } from '../utils/dateUtils';

const Auth = () => {
    const navigate = useNavigate();

    const handleLoginSuccess = async (firebaseUser) => {
        clientLogger.info('Auth: Firebase login success, preparing to sync with backend.', { 
            uid: firebaseUser.uid,
            email: firebaseUser.email 
        });

        try {
            const idToken = await firebaseUser.getIdToken();
            console.log('[Auth.jsx] Got Firebase ID token, length:', idToken.length);
            
            // This is a NEW endpoint we will need to create on the backend.
            const response = await fetch('/api/auth/firebase-login', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                    }
            });

            console.log('[Auth.jsx] Backend response status:', response.status);
            console.log('[Auth.jsx] Backend response ok:', response.ok);

            if (!response.ok) {
                const errorData = await response.json();
                console.error('[Auth.jsx] Backend response error:', errorData);
                throw new Error(errorData.error || 'Backend sync failed');
            }

            const responseData = await response.json();
            console.log('[Auth.jsx] Backend response data:', responseData);
            
            const { user: backendUser } = responseData;
            console.log('[Auth.jsx] Extracted user data:', backendUser);

            if (!backendUser || !backendUser.id) {
                console.error('[Auth.jsx] Invalid user data received:', backendUser);
                throw new Error('Invalid user data received from backend');
            }
        
            // Store our backend's user data, which may have more info (e.g., orgs)
            console.log('[Auth.jsx] Storing user data in localStorage...');
            localStorage.setItem('userData', JSON.stringify(backendUser));
            
            // Verify storage worked
            const storedData = localStorage.getItem('userData');
            console.log('[Auth.jsx] Verified localStorage storage:', !!storedData);
            console.log('[Auth.jsx] Stored data length:', storedData ? storedData.length : 0);

            // Navigate to the appropriate dashboard based on user role and current date
            console.log('[Auth.jsx] Determining correct dashboard...');
            
            // **ADMIN USERS: Always go to admin dashboard regardless of promotional periods**
            if (backendUser.is_admin) {
                console.log('[Auth.jsx] Admin user detected - navigating to admin dashboard...');
                navigate('/admin');
                return;
            }
            
            // **REGULAR USERS: Check for active promotional periods**
            const lbfActive = isLondonBookFairActive();
            const summerActive = isSummerWelcomeActive();
            
            console.log('[Auth.jsx] Dashboard selection for regular user:', {
                lbfActive,
                summerActive,
                currentDate: new Date().toISOString()
            });
            
            // Priority: LBF first, then Summer Welcome, then regular dashboard
            if (lbfActive) {
                console.log('[Auth.jsx] Navigating to LBF dashboard...');
                navigate('/lbf');
            } else if (summerActive) {
                console.log('[Auth.jsx] Navigating to Summer Welcome dashboard...');
                navigate('/summer-welcome');
            } else {
                console.log('[Auth.jsx] Navigating to regular dashboard...');
                navigate('/dashboard');
            }

        } catch (error) {
            console.error('[Auth.jsx] Authentication error:', error);
            clientLogger.error('Auth: Backend sync failed', error);
            // TODO: Show an error message to the user
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-white to-green-50">
            <div className="absolute inset-0 z-0 opacity-20" 
                style={{ 
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23065f46' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` 
                }}
            />

            <div className="relative w-full max-w-sm px-4">
                <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-600 text-white text-2xl font-bold mb-4">
                        C360
                    </div>
                    <h1 className="text-2xl font-bold text-gray-900">Welcome to C360</h1>
                    <p className="mt-2 text-gray-600">
                        Sign in to continue
                    </p>
                </div>

                <FirebaseLogin onSuccess={handleLoginSuccess} />
            </div>
        </div>
    );
};

export default Auth; 